import { useEffect, useRef, useState } from "react";
import { Canceler } from "axios";
import { useParams } from "react-router-dom";
import { useTrans } from "../../../hooks";
import {
    Button,
    Flex,
    Input,
    Modal,
    ModalBody,
    ModalCloseButton,
    ModalContent,
    ModalFooter,
    ModalHeader,
    ModalOverlay,
    Select,
    VStack,
    useDisclosure,
    useToast,
} from "@chakra-ui/react";
import dayjs from "dayjs";
import {
    AppAlertDelete,
    AppDatePicker,
    AppDateSelectSlider,
    AppDateSelectSliderDataType,
    AppFormControl,
    AppLoader,
    AppPageContent,
    AppPageHeader,
    AppPagination,
    AppSelectOption,
    AppTimesheetUserSelector,
    UserTimesheetsType,
} from "../../../components";
import {
    Internship,
    InternshipApi,
    InternshipSchedule,
    InternshipScheduleApi,
    Timesheet,
    TimesheetApi,
    TimesheetType,
    TimesheetTypeApi,
    TimeSlots,
    User,
    UserApi,
} from "../../../apis";
import {
    errorsDataType,
    errorsMessageDataType,
} from "../../../apis/entities/dtos";
import { APPC, cPath, UTIL } from "../../../config";

export const InternshipTimesheetPage = () => {
    // hook
    const { t } = useTrans();
    const toast = useToast();
    const { id: internshipId, internshipScheduleId: scheduleId } = useParams<{
        id: string;
        internshipScheduleId: string;
    }>();
    const { isOpen, onOpen, onClose } = useDisclosure();
    const deleteAlertState = useDisclosure();
    const cancelTokenSourcesRef = useRef<Canceler[]>([]);

    // state & const
    const dayjs = UTIL.getDayJs();
    const pageSize = 10;
    const [page, setPage] = useState(1);
    const [pagesCount, setPagesCount] = useState(0);
    const [users, setUsers] = useState<User[] | null>(null);
    const [timesheets, setTimesheets] = useState<UserTimesheetsType[] | null>(
        null
    );
    const [loading, setLoading] = useState(true);
    const [internship, setInternship] = useState<Internship | null>(null);
    const [internshipSchedule, setInternshipSchedule] =
        useState<InternshipSchedule | null>(null);
    const [dateSliderData, setDateSliderData] = useState<
        AppDateSelectSliderDataType[]
    >([]);
    const [selectedDate, setSelectedDate] = useState<Date>();
    const [isSelectedDateDisabled, setIsSelectedDateDisabled] = useState(false);
    const [timesheetTypeOptions, setTimesheetTypeOptions] =
        useState<AppSelectOption[]>();
    const [selectedTimesheetType, setSelectedTimesheetType] =
        useState<string>();
    const [errors, setErrors] = useState<errorsDataType>({
        timesheetType: false,
        startTime: false,
        endTime: false,
    });
    const [errorMessages, setErrorMessages] = useState<errorsMessageDataType>(
        {}
    );
    const [editId, setEditId] = useState(0);
    const [selectedStartTime, setSelectedStartTime] = useState<Date | null>();
    const [selectedEndTime, setSelectedEndTime] = useState<Date | null>();
    const [selectedUserTimesheets, setSelectedUserTimesheets] = useState<
        Timesheet[] | null
    >(null);
    const [selectedComment, setSelectedComment] = useState<string>("");
    const [actionSelectId, setActionSelectId] = useState(0);
    const [selectedUserId, setSelectedUserId] = useState<number | null>(null);

    // handler
    const chunkArray = <T,>(arr: T[], chunkSize: number): T[][] => {
        const result: T[][] = [];
        for (let i = 0; i < arr.length; i += chunkSize) {
            result.push(arr.slice(i, i + chunkSize));
        }
        return result;
    };

    const onValidate = (ids: number[], isChecked: boolean) => {
        const payload: Timesheet = {
            internshipSchedule: InternshipScheduleApi.toResourceIRI(scheduleId),
            isValidated: isChecked,
            timesheetIds: ids,
        } as Timesheet;

        TimesheetApi.postTimesheetValidation<Timesheet, Timesheet>(
            payload
        ).then(({ response, errorMessage }) => {
            if (errorMessage) {
                toast({
                    title: errorMessage,
                    status: "error",
                });
            } else if (response !== null) {
                toast({
                    title: t("cmn:message.save.success"),
                    status: "success",
                });
            }
        });
    };

    const calculateDateSliderData = (is: InternshipSchedule) => {
        const weekDays = is?.weekDays;
        const weekStart = dayjs(selectedDate)
            .subtract(dayjs(selectedDate).isoWeekday() - 1, "days")
            .set("hour", 0)
            .set("minute", 0)
            .set("second", 0);
        const wd: string[] = [];
        if (is.isScheduleDetail) {
            const cts = (is.timeSlots as TimeSlots).filter(
                (ts) => dayjs(`${ts[0].date} 00:00:00+00:00`) >= weekStart
            );
            cts[0].forEach((t) => {
                if (t.isChecked) {
                    wd.push(t.date as string);
                }
            });
        }

        const dsd: AppDateSelectSliderDataType[] = [];
        const startDate = dayjs(is?.startDate)
            .set("hour", 0)
            .set("minute", 0)
            .set("second", 0);
        const endDate = dayjs(is?.endDate)
            .set("hour", 0)
            .set("minute", 0)
            .set("second", 0);
        for (let i = 0; i < 7; i++) {
            const dt = dayjs(weekStart).add(i, "days");
            const isDisabled = is.isScheduleDetail
                ? !wd.includes(dt.format(APPC.APP_HTML_DATE_FORMAT))
                : !weekDays[UTIL.getWeekDayName(dt.format("d"))] ||
                  dt < startDate ||
                  dt > endDate;
            dsd.push({
                date: dt.toDate(),
                isDisabled: isDisabled,
            });
            if (
                dayjs(selectedDate).format(APPC.APP_HTML_DATE_FORMAT) ===
                dayjs(dt).format(APPC.APP_HTML_DATE_FORMAT)
            ) {
                setIsSelectedDateDisabled(isDisabled);
            }
        }
        setDateSliderData(dsd);
    };

    const addTimesheetHandler = (data: UserTimesheetsType) => {
        setSelectedUserId(data.id);
        setSelectedUserTimesheets(data.timesheets);
        setEditId(0);
        setSelectedTimesheetType("");
        setSelectedStartTime(null);
        setSelectedEndTime(null);
        setSelectedComment("");
        setErrors({
            timesheetType: false,
            startTime: false,
            endTime: false,
        });
        onOpen();
    };

    const rowEditHandler = (
        timesheet: Timesheet,
        userTimesheets: Timesheet[]
    ) => {
        setSelectedUserTimesheets(userTimesheets || []);
        setEditId(timesheet.id);
        setSelectedTimesheetType(
            (timesheet?.timesheetType as TimesheetType)?.id?.toString() || ""
        );
        setSelectedStartTime(
            timesheet.startTime
                ? dayjs(
                      (timesheet.startTime as string).replace("+00:00", "")
                  ).toDate()
                : null
        );
        setSelectedEndTime(
            timesheet.endTime
                ? dayjs(
                      (timesheet.endTime as string).replace("+00:00", "")
                  ).toDate()
                : null
        );
        setSelectedComment(timesheet?.comment || "");
        setErrors({
            timesheetType: false,
            startTime: false,
            endTime: false,
        });
        onOpen();
    };

    const transformDate = (value: string | Date): dayjs.Dayjs => {
        return dayjs(value)
            .set("year", 1970)
            .set("month", 0)
            .set("date", 1)
            .set("second", 0)
            .set("millisecond", 0);
    };

    const validate = () => {
        const newErrors = {
            timesheetType: false,
            startTime: false,
            endTime: false,
        };
        const newErrorMessages: errorsMessageDataType = {};
        if (!selectedTimesheetType) {
            newErrors.timesheetType = true;
            newErrorMessages.timesheetType = t("cmn:val.required.short");
        }
        if (!selectedStartTime) {
            newErrors.startTime = true;
            newErrorMessages.startTime = t("cmn:val.required.short");
        }
        if (!selectedEndTime) {
            newErrors.endTime = true;
            newErrorMessages.endTime = t("cmn:val.required.short");
        }
        if (selectedStartTime && selectedEndTime) {
            const start = transformDate(selectedStartTime);
            const end = transformDate(selectedEndTime);

            if (!end.isAfter(start)) {
                newErrors.endTime = true;
                newErrorMessages.endTime = t(
                    "ent.Timesheet:endTime.val.greaterThanStart"
                );
            } else {
                const hasConflict = selectedUserTimesheets
                    ?.filter((row) => row.id !== editId)
                    .some((row) => {
                        const existingStart = dayjs(
                            (row.startTime as string).replace("+00:00", "")
                        );
                        const existingEnd = dayjs(
                            (row.endTime as string).replace("+00:00", "")
                        );
                        const overlap = !(
                            end <= existingStart || start >= existingEnd
                        );
                        return overlap;
                    });
                if (hasConflict) {
                    newErrors.startTime = true;
                    newErrors.endTime = true;
                    newErrorMessages.startTime = t(
                        "ent.Timesheet:startTime.val.conflictsWithOthers"
                    );
                    newErrorMessages.endTime = t(
                        "ent.Timesheet:endTime.val.conflictsWithOthers"
                    );
                }
            }
        }
        setErrors(newErrors);
        setErrorMessages(newErrorMessages);
        return !Object.values(newErrors).some((e) => e);
    };

    const onTimesheetSubmitHandler = () => {
        if (!validate()) {
            return;
        }
        const timesheet: Timesheet = {
            startTime: `1970-01-01T${dayjs(selectedStartTime).format(APPC.APP_HTML_TIME_FORMAT)}+00:00`,
            endTime: `1970-01-01T${dayjs(selectedEndTime).format(APPC.APP_HTML_TIME_FORMAT)}+00:00`,
            timesheetType: TimesheetTypeApi.toResourceIRI(
                selectedTimesheetType
            ),
            comment: selectedComment,
        } as Timesheet;
        if (!editId) {
            timesheet.date = dayjs(selectedDate).format(
                APPC.APP_HTML_DATE_FORMAT
            );
            timesheet.user = UserApi.toResourceIRI(selectedUserId);
            timesheet.internshipSchedule =
                InternshipScheduleApi.toResourceIRI(scheduleId);
        }
        setLoading(true);
        TimesheetApi.createOrUpdate<Timesheet, Timesheet>(editId, timesheet)
            .then(({ response, errorMessage }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    toast({
                        title: t("cmn:message.save.success"),
                        status: "success",
                    });
                    fetchUsersAndTimesheets();
                }
            })
            .finally(() => {
                setLoading(false);
                onClose();
            });
    };

    const fetchInternshipAndInternshipSchedule = () => {
        setLoading(true);
        Promise.all([
            internshipId
                ? InternshipApi.getItem<Internship>(internshipId)
                : Promise.resolve(null),
            scheduleId
                ? InternshipScheduleApi.getItem<InternshipSchedule>(scheduleId)
                : Promise.resolve(null),
        ])
            .then(([internshipRes, scheduleRes]) => {
                if (internshipRes) {
                    if (internshipRes.errorMessage) {
                        toast({
                            title: internshipRes.errorMessage,
                            status: "error",
                        });
                    } else if (internshipRes.isNotFound) {
                        toast({
                            title: t("cmn:message.entityNotFound"),
                            status: "error",
                        });
                    } else if (internshipRes.response !== null) {
                        setInternship(internshipRes.response);
                    }
                }
                if (scheduleRes) {
                    if (scheduleRes.errorMessage) {
                        toast({
                            title: scheduleRes.errorMessage,
                            status: "error",
                        });
                    } else if (scheduleRes.isNotFound) {
                        toast({
                            title: t("cmn:message.entityNotFound"),
                            status: "error",
                        });
                    } else if (scheduleRes.response !== null) {
                        const is = scheduleRes.response;
                        setInternshipSchedule(is);

                        const startDate = dayjs(is?.startDate);
                        const endDate = dayjs(is?.endDate);
                        const now = new Date();
                        let sd = selectedDate || now;
                        if (now < startDate.toDate()) {
                            sd = startDate.toDate();
                        }
                        if (now > endDate.toDate()) {
                            sd = endDate.toDate();
                        }
                        setSelectedDate(sd);
                        calculateDateSliderData(is);
                    }
                }
            })
            .finally(() => setLoading(false));
    };

    const fetchUsersAndTimesheets = () => {
        setLoading(true);
        const params = {
            itemsPerPage: pageSize,
            "order[firstName]": "asc",
            "internshipStudents.internshipSchedule.id": `${scheduleId}`,
        };

        UserApi.getCollectionSchedule<User>(page, params, (c) => {
            cancelTokenSourcesRef.current.push(c);
        })
            .then(({ errorMessage, response }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else if (response !== null) {
                    setPagesCount(Math.ceil(response.totalItems / pageSize));
                    const tUsers = response.items;
                    setUsers(tUsers);
                    const uIds = tUsers.map((u) => u.id);

                    const params = {
                        pagination: false,
                        date: dayjs(selectedDate).format(
                            APPC.APP_HTML_DATE_FORMAT
                        ),
                        "internshipSchedule.id": `${scheduleId}`,
                        "order[user.firstName]": "asc",
                    };
                    params["user.id"] = uIds;

                    TimesheetApi.getCollectionForValidation<Timesheet>(
                        1,
                        params,
                        (c) => {
                            cancelTokenSourcesRef.current.push(c);
                        }
                    )
                        .then(({ errorMessage, response }) => {
                            if (errorMessage) {
                                toast({
                                    title: errorMessage,
                                    status: "error",
                                });
                            } else if (response !== null) {
                                const userMap: {
                                    [userId: number]: UserTimesheetsType;
                                } = {};

                                response.items?.forEach((t) => {
                                    const userMeta = (
                                        tUsers.filter(
                                            (u) => u["@id"] === t?.user["@id"]
                                        )[0] as User
                                    ).meta;
                                    const userId = userMeta?.id || 0;
                                    if (!userMap[userId]) {
                                        userMap[userId] = {
                                            id: userId,
                                            fullName: userMeta?.fullName,
                                            imageName: userMeta?.imageName,
                                            timesheets: [],
                                        };
                                    }
                                    userMap[userId].timesheets.push(t);
                                });
                                tUsers?.forEach((u) => {
                                    if (!userMap[u.id]) {
                                        userMap[u.id] = {
                                            id: u.id,
                                            fullName: u?.meta?.fullName,
                                            imageName: u?.meta?.imageName,
                                            timesheets: [],
                                        };
                                    }
                                });
                                setTimesheets(Object.values(userMap));
                            }
                        })
                        .finally(() => setLoading(false));
                }
            })
            .finally(() => setLoading(false));
        if (internshipSchedule) {
            calculateDateSliderData(internshipSchedule);
        }
    };

    const fetchTimesheetTypeData = async () => {
        await TimesheetTypeApi.getCollection<TimesheetType>(1, {
            pagination: false,
        }).then(({ errorMessage, response }) => {
            if (errorMessage) {
                toast({
                    title: errorMessage,
                    status: "error",
                });
            } else if (response !== null) {
                setTimesheetTypeOptions(
                    response.items.map((t) => {
                        return { value: t.id, label: t.name };
                    })
                );
            }
        });
    };

    const deleteHandler = async () => {
        if (actionSelectId < 1) {
            return;
        }
        setLoading(true);
        await TimesheetApi.deleteItem(actionSelectId)
            .then(({ errorMessage }) => {
                if (errorMessage) {
                    toast({
                        title: errorMessage,
                        status: "error",
                    });
                } else {
                    fetchUsersAndTimesheets();
                }
            })
            .finally(() => setLoading(false));
    };

    const renderTimesheetForm = () => {
        return (
            <Modal isOpen={isOpen} onClose={onClose} size={"md"}>
                <ModalOverlay />
                <ModalContent>
                    <ModalHeader>
                        {t("padm.TimesheetPage:text.pageTitle")}
                    </ModalHeader>
                    <ModalCloseButton />
                    <ModalBody>
                        <VStack gap={5}>
                            <AppFormControl
                                label={t("ent.Timesheet:timesheetType.label")}
                                isInvalid={errors.timesheetType}
                                message={errorMessages?.timesheetType}
                            >
                                <Select
                                    placeholder={t(
                                        "ent.Timesheet:timesheetType.placeholder"
                                    )}
                                    size="md"
                                    onChange={(e) => {
                                        setSelectedTimesheetType(
                                            e.currentTarget.value
                                        );
                                        setErrors((prev) => ({
                                            ...prev,
                                            timesheetType: false,
                                        }));
                                    }}
                                    value={selectedTimesheetType}
                                >
                                    {timesheetTypeOptions?.map((o) => (
                                        <option value={o.value} key={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </Select>
                            </AppFormControl>
                            <AppFormControl
                                label={t("ent.Timesheet:startTime.label")}
                                isInvalid={errors.startTime}
                                message={errorMessages?.startTime}
                            >
                                <AppDatePicker
                                    selected={selectedStartTime ?? null}
                                    onChange={(date) => {
                                        setSelectedStartTime(date);
                                        setErrors((prev) => ({
                                            ...prev,
                                            startTime: false,
                                        }));
                                    }}
                                    placeholderText={t(
                                        "ent.Timesheet:startTime.placeholder"
                                    )}
                                    showTimeSelect
                                    showTimeSelectOnly
                                    timeIntervals={15}
                                    timeCaption={t("cmn:label.time")}
                                    dateFormat={APPC.APP_DP_TIME_FORMAT}
                                    timeFormat={APPC.APP_DP_TIME_FORMAT}
                                    customInput={<Input />}
                                    isClearable={true}
                                />
                            </AppFormControl>
                            <AppFormControl
                                label={t("ent.Timesheet:endTime.label")}
                                isInvalid={errors.endTime}
                                message={errorMessages?.endTime}
                            >
                                <AppDatePicker
                                    selected={selectedEndTime ?? null}
                                    onChange={(date) => {
                                        setSelectedEndTime(date);
                                        setErrors((prev) => ({
                                            ...prev,
                                            endTime: false,
                                        }));
                                    }}
                                    placeholderText={t(
                                        "ent.Timesheet:endTime.placeholder"
                                    )}
                                    showTimeSelect
                                    showTimeSelectOnly
                                    timeIntervals={15}
                                    timeCaption={t("cmn:label.time")}
                                    dateFormat={APPC.APP_DP_TIME_FORMAT}
                                    timeFormat={APPC.APP_DP_TIME_FORMAT}
                                    customInput={<Input />}
                                    isClearable={true}
                                />
                            </AppFormControl>
                            <AppFormControl
                                label={t("ent.Timesheet:comment.label")}
                                isLoading={loading}
                            >
                                <Input
                                    type="text"
                                    placeholder={t(
                                        "ent.Timesheet:comment.placeholder"
                                    )}
                                    defaultValue={selectedComment}
                                    onBlur={(e) => {
                                        setSelectedComment(
                                            e.currentTarget.value
                                        );
                                    }}
                                />
                            </AppFormControl>
                        </VStack>
                    </ModalBody>
                    <ModalFooter>
                        <Button variant="outline" mr={2} onClick={onClose}>
                            {t("cmn:button.cancel")}
                        </Button>
                        <Button onClick={onTimesheetSubmitHandler}>
                            {t("cmn:button.save")}
                        </Button>
                    </ModalFooter>
                </ModalContent>
            </Modal>
        );
    };

    // useEffect
    useEffect(() => {
        fetchInternshipAndInternshipSchedule();
    }, [internshipId, scheduleId]);

    useEffect(() => {
        fetchUsersAndTimesheets();
    }, [selectedDate, page]);

    useEffect(() => {
        fetchTimesheetTypeData();
    }, []);

    return (
        <>
            <AppPageHeader
                title={t("padm.InternshipTimesheetPage:text.pageTitle")}
                breadcrumb={
                    internship?.name && internshipSchedule?.name
                        ? [
                              {
                                  label: internship?.name,
                                  url: cPath("ADMIN.INTERNSHIP_PAGE"),
                              },
                              {
                                  label: internshipSchedule?.name,
                                  url: cPath("ADMIN.INTERNSHIP_SCHEDULE_PAGE", {
                                      id: internshipId,
                                  }),
                              },
                          ]
                        : []
                }
            ></AppPageHeader>
            {loading ? (
                <AppLoader />
            ) : (
                <AppPageContent>
                    <>
                        <VStack p={6} gap={6} alignItems={"flex-start"}>
                            <AppDateSelectSlider
                                data={dateSliderData}
                                selectedDate={selectedDate || new Date()}
                                setSelectedDate={setSelectedDate}
                                start={dayjs(
                                    internshipSchedule?.startDate
                                ).toDate()}
                                end={dayjs(
                                    internshipSchedule?.endDate
                                ).toDate()}
                            />
                            {timesheets &&
                                chunkArray<UserTimesheetsType>(
                                    timesheets,
                                    5
                                ).map((chunk, chunkIndex) => (
                                    <Flex
                                        key={chunkIndex}
                                        width="full"
                                        alignItems={"start"}
                                        gap={4}
                                        overflowX={"auto"}
                                        wrap={"nowrap"}
                                    >
                                        {chunk?.map((u, index) => (
                                            <AppTimesheetUserSelector
                                                key={index}
                                                data={u}
                                                onAddTimesheet={
                                                    isSelectedDateDisabled
                                                        ? undefined
                                                        : addTimesheetHandler
                                                }
                                                onRowEdit={rowEditHandler}
                                                onValidate={onValidate}
                                                setActionSelectId={
                                                    setActionSelectId
                                                }
                                                deleteAlertState={
                                                    deleteAlertState
                                                }
                                            />
                                        ))}
                                    </Flex>
                                ))}
                            {pagesCount > 1 && users && users.length > 0 && (
                                <AppPagination
                                    onClick={(pageNumber) =>
                                        setPage(pageNumber)
                                    }
                                    totalPages={pagesCount}
                                    activePage={page}
                                />
                            )}
                        </VStack>
                        {isOpen && renderTimesheetForm()}
                        {deleteAlertState.isOpen && (
                            <AppAlertDelete
                                isOpenDelete={deleteAlertState.isOpen}
                                deleteHandler={() => deleteHandler()}
                                onCloseDelete={deleteAlertState.onClose}
                            />
                        )}
                    </>
                </AppPageContent>
            )}
        </>
    );
};
